import type { SensorType } from "../../../shared.js";
import { voltage5vClusterConfigs } from "./electricalVoltage/voltage5vClusterConfigs.js";
import { ePlantTreeTagClusterConfigs } from "./ePlant/ePlantTreeTagClusterConfigs.js";
import { badgermeterFlowmeterClusterConfigs } from "./flowmeters/badgermeterFlowmeterClusterConfigs.js";
import { flomecFlowmeterClusterConfigs } from "./flowmeters/flomecFlowmeterClusterConfigs.js";
import { genericFlowmeterClusterConfigs } from "./flowmeters/genericFlowmeterClusterConfigs.js";
import { mccrometerFlowmeterClusterConfigs } from "./flowmeters/mccrometerFlowmeterClusterConfigs.js";
import { verdiFlowmeterClusterConfigs } from "./flowmeters/verdiFlowmeterClusterConfigs.js";
import { hydrascoutSdi12ClusterConfigs } from "./hydrascout/hydrascoutSdi12ClusterConfigs.js";
import { batteryClusterConfigs } from "./internal/batteryClusterConfigs.js";
import { genericDiagnosticClusterConfigs } from "./internal/genericDiagnosticClusterConfigs.js";
import { networkStatsClusterConfigs } from "./internal/networkStatsClusterConfigs.js";
import { solenoidClusterConfigs } from "./internal/solenoidClusterConfigs.js";
import { tempHumClusterConfigs } from "./internal/tempHumClusterConfigs.js";
import { pressureTransducerClusterConfigs } from "./pressureTransducers/pressureTransducerClusterConfigs.js";
import { sensoterraClusterConfigs } from "./sensoterra/sensoterraClusterConfigs.js";
import { soilTensionClusterConfigs } from "./soilTension/soilTensionClusterConfigs.js";
import { genericTotalizerClusterConfigs } from "./totalizers/genericTotalizerClusterConfigs.js";
import { SensorClusterConfig } from "./types.js";
import { zoneVirtualClusterConfigs } from "./virtual/zoneVirtualClusterConfigs.js";

export const sensorClusterConfigs: Record<string, SensorClusterConfig<any>> = {
    // External physical sensors
    ...verdiFlowmeterClusterConfigs,
    ...badgermeterFlowmeterClusterConfigs,
    ...flomecFlowmeterClusterConfigs,
    ...mccrometerFlowmeterClusterConfigs,
    ...genericFlowmeterClusterConfigs,
    ...genericTotalizerClusterConfigs,
    ...pressureTransducerClusterConfigs,
    ...hydrascoutSdi12ClusterConfigs,
    ...ePlantTreeTagClusterConfigs,
    ...sensoterraClusterConfigs,
    ...soilTensionClusterConfigs,
    ...voltage5vClusterConfigs,

    // Internal physical sensors
    ...tempHumClusterConfigs,

    // Generic diagnostic sensors (shared across device generations)
    ...genericDiagnosticClusterConfigs,

    // Generation-specific diagnostic sensors
    ...batteryClusterConfigs,
    ...networkStatsClusterConfigs,
    ...solenoidClusterConfigs,

    // Zone virtual sensors
    ...zoneVirtualClusterConfigs,
};

/**
 * The cluster config key that claims the given legacy sensor type, or `null` if none does.
 *
 * A `null` result is consequential: `getConnectedSensorClusters` produces no `SensorCluster` for that
 * sensor, so no `SensorElement` is generated, no stream reservation is provisioned, and every reading
 * from that port is dropped at address resolution. In other words the sensor is dark — configured on
 * the device and reporting, but recording nothing.
 *
 * Exposed so callers can ask that question directly rather than re-deriving it, and so the
 * still-unmapped sensor types can be counted across the fleet.
 */
export function findSensorClusterConfigKeyForLegacySensorType(sensorType: SensorType): string | null {
    const match = Object.entries(sensorClusterConfigs).find(
        ([, config]) => config.migrationProperties?.legacySensorType === sensorType,
    );
    return match ? match[0] : null;
}
